/**
 * Displays a countdown to the cutoff time for the next available
 * delivery, based on provided data.
 *
 * Requires:
 * - AlpineJS in global scope (window.Alpine)
 * - DayJS in global scope    (window.dayjs)
 *
 * @author Joe Marsh
 * @version 1.0.0
 */

/**
 * @typedef {{
 *  data(key: string, def: CallableFunction): void;
 *  store(key: string, def: CallableFunction): void;
 * }} AlpineJS
 *
 * @typedef {{
 *  (input: number): DayJS;
 *  (): number;
 * }} DayJsGetterSetter
 *
 * @typedef {{
 *  clone(): DayJS;
 *  day: DayJsGetterSetter;
 *  hour: DayJsGetterSetter;
 *  minute: DayJsGetterSetter;
 *  second: DayJsGetterSetter;
 *  millisecond: DayJsGetterSetter;
 *  add(amount: number, unit: string): DayJS;
 *  isSame(date: DayJS, unit: string): boolean;
 *  isBefore(date: DayJS): boolean;
 *  isAfter(date: DayJS): boolean;
 *  diff(date: DayJS): number;
 * }} DayJS
 *
 * @typedef {string[]} ActiveDays
 * @typedef {number[]} ActiveDaysIndex
 *
 * @typedef {{
 *  d: number;
 *  h: number;
 *  m: number;
 *  s: number;
 * }} RemainingTime
 *
 * @typedef {{
 *  shippingDays: ActiveDays;
 *  deliveryDays: ActiveDays;
 *  skippedShippingDates: string[];
 *  skippedDeliveryDates: string[];
 *  orderCutoffHour: number;
 *  handlingTimeDays: number;
 *  shippingTimeDays: number;
 * }} InitOptions
 */

document.addEventListener('alpine:init', function () {
	const Alpine = /** @type {AlpineJS} */ (window['Alpine']);
	const dayjs = /** @type {() => DayJS} */ (window['dayjs']);

	// -- Helpers --------

	/**
	 * Converts a weekday string to an integer. 0 = Sunday.
	 *
	 * @param {string} weekday
	 * @returns {number}
	 */
	function weekdayStringToInt(weekday) {
		return [
			'sunday',
			'monday',
			'tuesday',
			'wednesday',
			'thursday',
			'friday',
			'saturday',
		].indexOf(weekday.toLowerCase());
	}

	/**
	 * Returns a cloned DayJS object, but with the time set to
	 * 00:00:00.000, or the specified hours/minutes/seconds/milliseconds.
	 *
	 * @param {DayJS} date the DayJS object to reset the time of
	 * @param {number} hour the hour to set the time to
	 * @param {number} minute the minute to set the time to
	 * @param {number} second the second to set the time to
	 * @param {number} millisecond the millisecond to set the time to
	 * @returns {DayJS}
	 *
	 */
	function resetDateTime(
		date,
		hour = 0,
		minute = 0,
		second = 0,
		millisecond = 0
	) {
		return date
			.clone()
			.hour(hour)
			.minute(minute)
			.second(second)
			.millisecond(millisecond);
	}

	/**
	 * Returns the difference between two dates in days,
	 * hours, minutes and seconds.
	 *
	 * @param {DayJS} from the starting date
	 * @param {DayJS} to the ending date
	 * @returns {RemainingTime}
	 */
	function getDiff(from, to) {
		const diffMS = to.diff(from);
		return {
			d: Math.floor(diffMS / (1000 * 60 * 60 * 24)),
			h: Math.floor((diffMS % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
			m: Math.floor((diffMS % (1000 * 60 * 60)) / (1000 * 60)),
			s: Math.floor((diffMS % (1000 * 60)) / 1000),
		};
	}

	/**
	 * Returns true if the specified date is in the supplied
	 * list of active weekdays.
	 *
	 * @param {DayJS} date the date to check
	 * @param {ActiveDaysIndex} activeDays the list of active weekdays
	 * @returns {boolean}
	 */
	function isActiveDay(date, activeDays) {
		return activeDays.includes(date.day());
	}

	/**
	 * Returns true if the specified date is in the supplied
	 * list of skipped dates.
	 *
	 * @param {DayJS} date the date to check
	 * @param {DayJS[]} skippedDates the list of skipped dates
	 * @returns {boolean}
	 */
	function isSkippedDate(date, skippedDates) {
		return skippedDates.some(skippedDate => skippedDate.isSame(date, 'day'));
	}

	/**
	 * Returns true if the current time is after the specified
	 * shipping cutoff hour and the target date is the same as
	 * the current date.
	 *
	 * @param {DayJS} now the current date and time
	 * @param {DayJS} targetDate the target date to check
	 * @param {number} cutoffHour the shipping cutoff hour
	 * @returns {boolean}
	 */
	function isAfterCutoffHour(now, targetDate, cutoffHour) {
		return now.hour() >= cutoffHour && now.isSame(targetDate, 'day');
	}
  
  /**
	 * Returns true if the specified date is in the future.
	 *
	 * @param {DayJS} date the date to check
	 * @returns {boolean}
	 */
  function isInFuture(date) {
    return date.isAfter(dayjs());
  }

	/**
	 * Recursively gets the next available date based on the the
	 * supplied list of active weekdays and the list of skipped
	 * dates. Allows the hour of the returned date to be specified.
	 *
	 * @param {DayJS} targetDate the date to check
	 * @param {ActiveDaysIndex} activeDays the list of active weekdays
	 * @param {DayJS[]} skippedDates the list of skipped dates
	 * @param {number} setHour the hour to set the time to
	 * @returns {DayJS}
	 */
	function getNextAvailableDate(
		targetDate,
		activeDays,
		skippedDates,
		setHour = 0
	) {
		if (
			isActiveDay(targetDate, activeDays) &&
			!isSkippedDate(targetDate, skippedDates)
		) {
			return resetDateTime(targetDate, setHour);
		}
		return getNextAvailableDate(
			targetDate.add(1, 'day'),
			activeDays,
			skippedDates,
			setHour
		);
	}

	/**
	 * Wraps getNextAvailableDate() to get the next valid shipping
	 * date, but compares the cutoff time to the current time and
	 * recursively gets the next available date if the cutoff time
	 * has passed.
	 *
	 * @param {DayJS} now the current date and time
	 * @param {ActiveDaysIndex} shippingDays the list of active weekdays
	 * @param {DayJS[]} skippedShippingDates the list of skipped dates
	 * @param {number} cutoffHour the shipping cutoff hour
	 * @returns {DayJS}
	 */
	function getNextValidShippingDateTime(
		now,
		shippingDays,
		skippedShippingDates,
		cutoffHour
	) {
		const targetDate = resetDateTime(now);
		let shippingDate = getNextAvailableDate(
			targetDate,
			shippingDays,
			skippedShippingDates,
			cutoffHour
		);
		while (isAfterCutoffHour(now, shippingDate, cutoffHour)) {
			shippingDate = getNextAvailableDate(
				targetDate.add(1, 'day'),
				shippingDays,
				skippedShippingDates,
				cutoffHour
			);
		}
		return shippingDate;
	}

	// -- Component --------

	Alpine.data(
		'orderDeadline',
		/** @param {InitOptions} options */
		options => {
      if(!options.shippingDays) {
        options.shippingDays = [];
      }
      if(!options.deliveryDays) {
        options.deliveryDays = [];
      }
      if(!options.skippedShippingDates) {
        options.skippedShippingDates = [];
      }
      if(!options.skippedDeliveryDates) {
        options.skippedDeliveryDates = [];
      }
			const shippingDays = options.shippingDays.map(weekdayStringToInt);
			const deliveryDays = options.deliveryDays.map(weekdayStringToInt);
			const skippedShippingDates = options.skippedShippingDates
				.map(dayjs)
				.filter(isInFuture);
			const skippedDeliveryDates = options.skippedDeliveryDates
				.map(dayjs)
				.filter(isInFuture);
			const orderCutoffHour = options.orderCutoffHour;
      const handlingTimeDays = options.handlingTimeDays ? options.handlingTimeDays : 0;
      const shippingTimeDays = options.shippingTimeDays ? options.shippingTimeDays : 1;
			return {
				/** @type {DayJS|null} */
				nextShippingDate: null,
				/** @type {DayJS|null} */
				nextDeliveryDate: null,
				remaining: {
					d: 0,
					h: 0,
					m: 0,
					s: 0,
				},
				get hasValidDates() {
					return this.nextShippingDate && this.nextDeliveryDate;
				},
				get days() {
					return this.remaining.d;
				},
				get hours() {
					return this.remaining.h;
				},
				get minutes() {
					return this.remaining.m;
				},
				get seconds() {
					return this.remaining.s;
				},

				get renderDays() {
					return this.days > 0;
				},
				get renderHours() {
					return this.hours > 0;
				},
				get renderMinutes() {
					return !this.renderDays;
				},
				get renderSeconds() {
					return !this.renderDays && !this.renderHours;
				},

				/**
				 * Calculates the next available shipping and delivery dates
				 * relative to the current date and time.
				 */
				runDateUpdates() {
					const now = dayjs().add(handlingTimeDays, 'day');
					this.nextShippingDate = getNextValidShippingDateTime(
						now,
						shippingDays,
						skippedShippingDates,
						orderCutoffHour
					);
					this.nextDeliveryDate = getNextAvailableDate(
						this.nextShippingDate.add(shippingTimeDays, 'day'),
						deliveryDays,
						skippedDeliveryDates
					);
					this.remaining = getDiff(now, this.nextShippingDate);
				},

				/**
				 * Trigger the timer to update the remaining time. If more than
				 * 2 hours left, update every 5 seconds, otherwise update every
				 * second.
				 */
				init() {
					this.runDateUpdates();
					setInterval(
						() => {
							this.runDateUpdates();
						},
						this.hours > 2 ? 5000 : 1000
					);
				},
			};
		}
	);
});
